import { StatusData } from "@fairfleet/geotab";
import { TachographDeviceInfo, TachographManufacturer } from "../models/Tachograph";

/** The numbers the diagnostic "Tachograph manufacturer" reports. */
const MANUFACTURERS: Record<number, TachographManufacturer> = {
  0: "notAvailable",
  1: "unknown",
  2: "vdo",
  3: "stoneridge",
  4: "actia",
  5: "efas",
};

/** A calibration date outside this range is a misread value, not a date. */
const EARLIEST_CALIBRATION_MS = Date.UTC(2000, 0, 1);
const LATEST_CALIBRATION_MS = Date.UTC(2100, 0, 1);

export function toManufacturer(value: unknown): TachographManufacturer | null {
  return typeof value === "number" ? (MANUFACTURERS[value] ?? null) : null;
}

/** The tachograph reports the next calibration as seconds since 1970. */
export function toCalibrationDate(value: unknown): Date | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;

  const milliseconds = value * 1000;
  if (milliseconds < EARLIEST_CALIBRATION_MS || milliseconds > LATEST_CALIBRATION_MS) return null;

  return new Date(milliseconds);
}

type DiagnosticIds = {
  manufacturerId?: string;
  calibrationId?: string;
};

/** The newest reading of each diagnostic per vehicle. */
export function buildDeviceInfo(
  rows: StatusData[],
  { manufacturerId, calibrationId }: DiagnosticIds,
): Map<string, TachographDeviceInfo> {
  const newest = new Map<string, { manufacturer?: number; calibration?: number; times: Map<string, number> }>();

  for (const row of rows) {
    const deviceId = row.device?.id;
    const diagnosticId = row.diagnostic?.id;
    const time = row.dateTime ? new Date(row.dateTime).getTime() : NaN;
    if (!deviceId || !diagnosticId || Number.isNaN(time) || typeof row.data !== "number") continue;

    const entry = newest.get(deviceId) ?? { times: new Map<string, number>() };
    const previousTime = entry.times.get(diagnosticId);
    if (previousTime !== undefined && previousTime >= time) continue;

    entry.times.set(diagnosticId, time);
    if (diagnosticId === manufacturerId) entry.manufacturer = row.data;
    if (diagnosticId === calibrationId) entry.calibration = row.data;
    newest.set(deviceId, entry);
  }

  const info = new Map<string, TachographDeviceInfo>();
  for (const [deviceId, entry] of newest) {
    info.set(deviceId, {
      manufacturer: toManufacturer(entry.manufacturer),
      nextCalibration: toCalibrationDate(entry.calibration),
    });
  }

  return info;
}
